/**
 * Generate a deterministic avatar SVG URL for a user without a profile image.
 * Creates a colorful geometric pattern similar to GitHub/Reddit default avatars.
 */
export function generateRandomAvatar(seed: string): string {
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  // Generate colors from hash
  const hue = Math.abs(hash % 360);
  const saturation = 60 + Math.abs((hash >> 8) % 30);
  const lightness = 45 + Math.abs((hash >> 16) % 20);
  const bgColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  const fgColor = `hsl(${(hue + 180) % 360}, ${saturation}%, ${Math.min(lightness + 30, 90)}%)`;

  // Generate 5x5 symmetric grid
  const grid: boolean[][] = [];
  for (let row = 0; row < 5; row++) {
    grid[row] = [];
    for (let col = 0; col < 3; col++) {
      const bit = Math.abs((hash >> (row * 3 + col)) % 2) === 1;
      grid[row][col] = bit;
      // Mirror for symmetry
      grid[row][4 - col] = bit;
    }
  }

  // Generate SVG
  const cellSize = 50;
  const padding = 25;
  const totalSize = cellSize * 5 + padding * 2;

  let rects = '';
  for (let row = 0; row < 5; row++) {
    for (let col = 0; col < 5; col++) {
      if (grid[row][col]) {
        rects += `<rect x="${padding + col * cellSize}" y="${padding + row * cellSize}" width="${cellSize}" height="${cellSize}" fill="${fgColor}"/>`;
      }
    }
  }

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalSize}" height="${totalSize}" viewBox="0 0 ${totalSize} ${totalSize}"><rect width="${totalSize}" height="${totalSize}" fill="${bgColor}"/>${rects}</svg>`;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
}
